import { useCallback, useEffect, useRef, useState } from 'react';
import { useBillingClient } from 'client/client-hook';
import useAuth from 'hooks/useAuth';
import { getFreezeErrorMessage } from 'features/subscription-freeze/formatters';
import { createClientIdempotencyKey } from 'features/subscription-freeze/storage';
import { withTimeout } from 'features/subscription-freeze/withTimeout';
import { useBillingStore } from 'sections/account-settings/subscriptions/checkout-v2/store';
import { CancelOffer, ReadyCancelOffer } from './offers';

// A customer waits on this between the reason and the next step.
const CANCEL_OFFER_TIMEOUT_MS = 8_000;

// Parts of billing-manager's refusals on accepting: no plan can take it any more, or the account already
// took its one offer (still waiting, or used).
const BILLING_CHANGED_ERRORS = ['cancel_offer_no_eligible_plans'];
const ALREADY_TAKEN_ERRORS = ['already used', 'cancel_offer_already_used', 'cancel_offer_waiting'];
const refusedFor = (message: string, parts: string[]) => parts.some((part) => message.includes(part));

export type CancelOfferState =
  | { status: 'idle' }
  | { status: 'loading' }
  // `waiting`: billing's answer when an offer they took earlier still waits for their next payment.
  | { status: 'none'; waiting?: CancelOffer }
  | { status: 'ready'; offer: ReadyCancelOffer };
export type AcceptResult =
  | { outcome: 'accepted' }
  | { outcome: 'billingChanged' }
  | { outcome: 'alreadyTaken' }
  | { outcome: 'failed'; message: string };

const IDLE: CancelOfferState = { status: 'idle' };
const LOADING: CancelOfferState = { status: 'loading' };
const NONE: CancelOfferState = { status: 'none' };

/**
 * The discount offered to someone leaving over price, asked for once `active` first turns on (as soon as
 * they pick the reason) and again on `reload`. Any failure offers none, so the cancel is never held up.
 */
export function useCancelOffer(active: boolean) {
  const { user } = useAuth() as any;
  const userId: string = user?.tcId || '';
  const getBillingClient = useBillingClient();
  const latestClient = useRef(getBillingClient);
  latestClient.current = getBillingClient;
  const fetchBilling = useBillingStore((state) => state.fetch);
  const [state, setState] = useState<CancelOfferState>(IDLE);
  const [asked, setAsked] = useState(0);
  const fetched = useRef(-1);
  // Only the latest request may answer; an older one, or one landing after close, is dropped.
  const latestRequest = useRef(0);
  const mounted = useRef(true);
  // One per visit: a retry after a lost reply finishes the same acceptance instead of a second one.
  const idempotencyKey = useRef(createClientIdempotencyKey());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Not cancelled when `active` turns off: picking another reason mid-request must still keep the answer.
  useEffect(() => {
    if (!active || !userId || fetched.current === asked) return;
    fetched.current = asked;
    const request = ++latestRequest.current;
    const answer = (next: CancelOfferState) => {
      if (mounted.current && request === latestRequest.current) setState(next);
    };
    // A reload keeps the offer on screen until the new one lands.
    setState((current) => (current.status === 'ready' ? current : LOADING));
    (async () => {
      try {
        const client = await latestClient.current();
        const offer = (await withTimeout(client.getCancelOffer({ userId }), CANCEL_OFFER_TIMEOUT_MS)) as CancelOffer;
        answer(
          offer.eligible && offer.nextCharge
            ? { status: 'ready', offer: offer as ReadyCancelOffer }
            : { status: 'none', waiting: offer.waitingOffer ? offer : undefined }
        );
      } catch {
        answer(NONE);
      }
    })();
  }, [active, userId, asked]);

  const reload = useCallback(() => setAsked((count) => count + 1), []);

  const accept = async (): Promise<AcceptResult> => {
    setBusy(true);
    try {
      const client = await latestClient.current();
      await client.acceptCancelOffer({ userId, idempotencyKey: idempotencyKey.current });
      void fetchBilling(userId, { force: true });
      return { outcome: 'accepted' };
    } catch (cause) {
      const message = getFreezeErrorMessage(cause);
      if (refusedFor(message, BILLING_CHANGED_ERRORS)) return { outcome: 'billingChanged' };
      if (refusedFor(message, ALREADY_TAKEN_ERRORS)) return { outcome: 'alreadyTaken' };
      return { outcome: 'failed', message };
    } finally {
      setBusy(false);
    }
  };

  return { state, reload, accept, busy };
}
